import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react";

import { CLOCK_CADENCE } from "./clock-cadences";
import { WallClockMountedContext } from "./wall-clock-provider";
import { runWallClockCallback, wallClockService } from "./wall-clock-service";

function useRequireWallClockProvider(hookName: string): void {
    const isMounted = useContext(WallClockMountedContext);
    if (process.env.NODE_ENV !== "production" && !isMounted) {
        throw new Error(`${hookName} must be used inside WallClockProvider`);
    }
}

/**
 * Hook that returns the last tick time (epoch ms) for a given cadence.
 *
 * Re-renders the component when that time changes for the cadence.
 */
export function useWallClock(cadence: CLOCK_CADENCE): number {
    useRequireWallClockProvider("useWallClock");
    // Memoized subscribe fns so we dont recreate every render - useSyncExternalStore re-subscribes when `subscribe` changes reference.
    const subscribe = useCallback((listener: () => void) => wallClockService.subscribe(cadence, listener), [cadence]);
    const getSnapshot = useCallback(() => wallClockService.getTickTime(cadence), [cadence]);
    const getServerSnapshot = useCallback(() => 0, []);

    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Hook that calls a custom callback fn at every "tick" for the given cadence.
 *
 * Subscribes the callback rather than the component, so the host does not re-render on a tick.
 * A null cadence never runs the callback. Taking up a cadence — first render, resuming from null,
 * or moving to another cadence — runs it per `runOnMount`; nothing else does.
 */
export function useWallClockEffect(
    cadence: CLOCK_CADENCE | null,
    callback: () => void,
    { runOnMount = true }: { runOnMount?: boolean } = {}, // Default to true so it fires once on mount.
): void {
    useRequireWallClockProvider("useWallClockEffect");
    /**
     * Use a ref to keep the latest callback without it being a reactive dep, so callers
     * don't need to memoize and we avoid stale-closure bugs.
     */
    const callbackRef = useRef(callback);
    // The listener reads this outside React, so the ref must hold a committed callback.
    useLayoutEffect(() => {
        callbackRef.current = callback;
    });
    // Not a dep: flipping the option is not a tick, and must not run the callback.
    const runOnMountRef = useRef(runOnMount);
    runOnMountRef.current = runOnMount;

    useEffect(() => {
        if (cadence === null) {
            return undefined;
        }
        // Contained like a tick, so a throw here still leaves the caller subscribed for the next one.
        if (runOnMountRef.current) {
            runWallClockCallback(callbackRef.current);
        }
        return wallClockService.subscribe(cadence, () => callbackRef.current());
    }, [cadence]);
}
